import { Box, Center, HeadingProps } from '@chakra-ui/react';
import { NodeRenderer } from '@contentful/rich-text-react-renderer';
import React, { Suspense } from 'react';

import {
  WLH1,
  WLH2,
  WLH2V2,
  WLH3,
  WLH4,
  WLH5,
  WLH6,
  WLListItem,
  WLOrderedList,
  WLTable,
  WLTd,
  WLText,
  WLTh,
  WLTr,
  WLUnorderedList,
} from '../../design-library';
import ContentfulUtils from '../../utils/contentful.utils';
import {
  AccordionGrouper,
  Mode2Grouper,
  Mode3Grouper,
} from './RichText.groupers';
import { RichTextMode, RichTextOptions } from './types';
const WLDivider = React.lazy(() => import('../../design-library/Divider'));
const WLHeadlineDividerV2 = React.lazy(
  () => import('../../design-library/HeadlineDividerV2'),
);
const WLLink = React.lazy(() => import('../../design-library/Link'));
const DividerColorScheme = React.lazy(
  () => import('../../design-library/Divider'),
);
const ActionAdapter = React.lazy(() => import('../ActionAdapter'));
const EmbeddedVideoAdapter = React.lazy(
  () => import('../EmbeddedVideoAdapter'),
);
const FormAdapter = React.lazy(() => import('../FormAdapter'));
const GalleryAdapter = React.lazy(() => import('../GalleryAdapter'));
const ImageAdapter = React.lazy(() => import('../ImageAdapter'));
const LinkedImageAdapter = React.lazy(() => import('../LinkedImageAdapter'));
const SpecialElementAdapter = React.lazy(
  () => import('../SpecialElementAdapter'),
);
const TableAdapter = React.lazy(() => import('../TableAdapter'));

const HeadingMap = {
  h1: WLH1,
  h2: WLH2,
  h3: WLH3,
  h4: WLH4,
  h5: WLH5,
  h6: WLH6,
};
type HeadingLevel = keyof typeof HeadingMap;

const TEXT_TOKENS = /(.*?)(?:\[([\d†‡*]{1,3}|[®©])]|{{(.+?)}})(.*)/;

const shouldUseDividerForHeading = (
  level: HeadingLevel,
  mode: RichTextMode | undefined,
): boolean => mode === RichTextMode.MODE1 && ['h2', 'h3'].includes(level);

type MarginTopType = HeadingProps['marginTop'];
const HeadingRenderer = (
  level: HeadingLevel,
  marginTop: MarginTopType,
  { mode, isContent }: RichTextOptions,
): NodeRenderer => {
  const HeadingElement = HeadingMap[level];

  return (node, children) => {
    if (shouldUseDividerForHeading(level, mode)) {
      return (
        <Suspense fallback={<></>}>
          <WLDivider as={level} colorScheme={DividerColorScheme.MIXED}>
            {children}
          </WLDivider>
        </Suspense>
      );
    }
    return (
      <HeadingElement
        textAlign={mode === RichTextMode.CENTERED ? 'center' : undefined}
        marginTop={isContent ? marginTop : undefined}
      >
        {children}
      </HeadingElement>
    );
  };
};

export const BoldTextRenderer = (text: React.ReactNode) => (
  <WLText as="b">{text}</WLText>
);
export const ItalicTextRenderer = (text: React.ReactNode) => (
  <WLText as="i">{text}</WLText>
);
export const UnderlineTextRenderer = (text: React.ReactNode) => (
  <WLText as="u">{text}</WLText>
);
export const CodeTextRenderer = (text: React.ReactNode) => (
  <WLText as="code">{text}</WLText>
);
export const ParagraphRenderer: NodeRenderer = (node, children) => {
  if (React.Children.count(children) === 0) {
    return null;
  }
  return <WLText as="p">{children}</WLText>;
};
export const HorizontalRuleRenderer: NodeRenderer = (node, children) => (
  <Suspense fallback={<></>}>
    <WLDivider colorScheme={DividerColorScheme.GRAY}>{children}</WLDivider>
  </Suspense>
);
export const BlockquoteRenderer: NodeRenderer = (node, children) => (
  <WLText as="blockquote">{children}</WLText>
);
export const Heading1Renderer = (options: RichTextOptions): NodeRenderer =>
  HeadingRenderer('h1', 6, options);
export const Heading2Renderer = (options: RichTextOptions): NodeRenderer =>
  HeadingRenderer('h2', 5, options);
export const Heading3Renderer = (options: RichTextOptions): NodeRenderer =>
  HeadingRenderer('h3', 4, options);
export const Heading4Renderer = (options: RichTextOptions): NodeRenderer =>
  HeadingRenderer('h4', 3, options);
export const Heading5Renderer = (options: RichTextOptions): NodeRenderer =>
  HeadingRenderer('h5', 2, options);
export const Heading6Renderer = (options: RichTextOptions): NodeRenderer =>
  HeadingRenderer('h6', 1, options);
export const OrderedListRenderer: NodeRenderer = (node, children) => (
  <WLOrderedList>{children}</WLOrderedList>
);
export const UnorderedListRenderer: NodeRenderer = (node, children) => (
  <WLUnorderedList>{children}</WLUnorderedList>
);
export const ListItemRenderer: NodeRenderer = (node, children) => (
  <WLListItem>{children}</WLListItem>
);
export const TableRenderer: NodeRenderer = (node, children) => (
  <WLTable>{children}</WLTable>
);
export const TableRowRenderer: NodeRenderer = (node, children) => (
  <WLTr>{children}</WLTr>
);
export const TableHeaderCellRenderer: NodeRenderer = (node, children) => (
  <WLTh>{children}</WLTh>
);
export const TableCellRenderer: NodeRenderer = (node, children) => (
  <WLTd>{children}</WLTd>
);

const EmbeddedEntryRenderer: NodeRenderer = (node, children) => {
  const entryType = node.data?.target?.__typename;

  if (entryType === 'ContentfulLinkedImage') {
    return (
      <Center>
        <Suspense fallback={<></>}>
          <LinkedImageAdapter data={node.data.target} />
        </Suspense>
      </Center>
    );
  }

  if (entryType === 'ContentfulTable') {
    return (
      <Center width="full" paddingY={3}>
        <Suspense fallback={<></>}>
          <TableAdapter data={node.data.target} />
        </Suspense>
      </Center>
    );
  }
  if (entryType === 'ContentfulAction') {
    return (
      <Center width="full" paddingY={3} className="RichTextAction">
        <Suspense fallback={<></>}>
          <ActionAdapter data={node.data.target} />
        </Suspense>
      </Center>
    );
  }
  if (entryType === 'ContentfulEmbeddedVideo') {
    return (
      <Center width="full" paddingY={2}>
        <Suspense fallback={<></>}>
          <EmbeddedVideoAdapter data={node.data.target} />
        </Suspense>
      </Center>
    );
  }
  if (entryType === 'ContentfulFormReference') {
    return (
      <Center width="full">
        <Suspense fallback={<></>}>
          <FormAdapter data={node.data.target} />
        </Suspense>
      </Center>
    );
  }
  if (entryType === 'ContentfulGallery') {
    return (
      <Center width="full" paddingY={4}>
        <Suspense fallback={<></>}>
          <GalleryAdapter data={node.data.target} />
        </Suspense>
      </Center>
    );
  }
  if (entryType === 'ContentfulSpecialElement') {
    return (
      <Suspense fallback={<></>}>
        <SpecialElementAdapter
          item={node.data.target.item}
          content={node.data.target.content}
        />
      </Suspense>
    );
  }
  if (entryType === 'ContentfulHeadlineDivider') {
    return (
      <Suspense fallback={<></>}>
        {node.data.target.headlineText && (
          <WLH2V2
            fontSize={{ base: '5xl', lg: '56px' }}
            textAlign={'center'}
            color={`blue.800`}
            marginTop={'0px !important'}
          >
            <WLHeadlineDividerV2>
              {node.data.target.dividerText}
            </WLHeadlineDividerV2>
            {node.data.target.headlineText}
            <sup>{node.data.target.headlineSuperscript}</sup>
          </WLH2V2>
        )}
      </Suspense>
    );
  }
  console.warn(`Unsupported embedded entry: ${node?.data?.target?.__typename}`);
  return null;
};

export const EmbeddedAssetBlockRenderer: NodeRenderer = (node, children) => {
  // Note: custom class used to un-center images within Hero content blocks
  return (
    <Center width="full" className="RichTextAssetBlock" paddingY={3}>
      <Suspense fallback={<></>}>
        <ImageAdapter data={ContentfulUtils.getAssetInfo(node.data.target)} />
      </Suspense>
    </Center>
  );
};
export const EmbeddedEntryBlockRenderer = EmbeddedEntryRenderer;
export const EmbeddedEntryInlineRenderer = EmbeddedEntryRenderer;

export const HyperlinkRenderer: NodeRenderer = (node, children) => (
  <Suspense fallback={<></>}>
    <WLLink to={node.data.uri}>{children}</WLLink>
  </Suspense>
);
export const EntryHyperlinkRenderer: NodeRenderer = (node, children) => (
  <Suspense fallback={<></>}>
    <WLLink to={node.data.target.slug}>{children}</WLLink>
  </Suspense>
);
export const AssetHyperlinkInlineRenderer: NodeRenderer = (node, children) => (
  <Suspense fallback={<></>}>
    <WLLink to={ContentfulUtils.getAssetInfo(node.data.target)?.url || '#'}>
      {children}
    </WLLink>
  </Suspense>
);

export const TextRenderer = (text: string) => {
  let keyIter = 0;
  /*
  Take pure text within RichText blocks and process it for special handling.
  This block ensures that line-breaks within paragraphs are preserved by inserted `br` elements
  It also handles our special footnote reference pattern to add superscript formatting
   */
  const lines = text.split('\n');
  return lines.reduce((children, textSegment, index) => {
    const newSegments = [...children];
    if (textSegment) {
      let matches;
      let processedString = textSegment;
      while ((matches = TEXT_TOKENS.exec(processedString)) && matches) {
        matches[1] && newSegments.push(matches[1]);
        if (matches[2]) {
          newSegments.push(<sup key={`sup-${keyIter++}`}>{matches[2]}</sup>);
        } else if (matches[3]) {
          newSegments.push(
            <small key={`small-${keyIter++}`}>{matches[3]}</small>,
          );
        }
        processedString = matches[4];
      }
      if (processedString) {
        newSegments.push(processedString);
      }
    }

    if (index < lines.length - 1) {
      newSegments.push(<br key={`break-${keyIter++}`} />);
    }
    return newSegments;
  }, [] as React.ReactNode[]);
};

export const GroupingRenderer = ({
  size,
  alignment,
  mode,
}: RichTextOptions): NodeRenderer => {
  return (node, children) => {
    let result = children;

    if (Array.isArray(children)) {
      if (mode === RichTextMode.MODE2) {
        result = Mode2Grouper(children);
      } else if (mode === RichTextMode.MODE3) {
        result = Mode3Grouper(children);
      } else if (mode === RichTextMode.ACCORDION) {
        result = AccordionGrouper(children);
      }
    }

    return (
      <Box
        fontSize={size ? `${size / 100.0}em` : undefined}
        textAlign={alignment}
        width="full"
        maxWidth={{ base: '90vw', md: undefined }}
        className="richTextDiv"
      >
        {result}
      </Box>
    );
  };
};
